/**
 * Where a knowledge item stands, in words: one stage per item for its badge and the list filters, and
 * the small text helpers the editor uses for its one-per-line fields.
 */

import type { KnowledgeArticleLink, KnowledgeEvent, KnowledgeItem, KnowledgeSource } from './types';

export type Stage =
	| 'off'
	| 'auto_send'
	| 'draft'
	| 'edit_waiting'
	| 'serving_check_failed'
	| 'article_changed'
	| 'check_failed'
	| 'needs_check'
	| 'ready';
export type BadgeVariant = 'success' | 'info' | 'warning' | 'destructive' | 'secondary' | 'outline';

const STAGES: Record<Stage, { label: string; variant: BadgeVariant }> = {
	off: { label: 'Switched off', variant: 'outline' },
	auto_send: { label: 'Auto-sends', variant: 'success' },
	draft: { label: 'Serves as draft', variant: 'secondary' },
	edit_waiting: { label: 'Edit waiting', variant: 'warning' },
	serving_check_failed: { label: 'Serving, Check failed', variant: 'destructive' },
	article_changed: { label: 'Article changed', variant: 'warning' },
	check_failed: { label: 'Check failed', variant: 'destructive' },
	needs_check: { label: 'Needs Check', variant: 'warning' },
	ready: { label: 'Ready to promote', variant: 'outline' }
};

export const stageOf = (item: KnowledgeItem): Stage => {
	if (item.state === 'promoted') {
		if (!item.enabled) return 'off';
		if (item.article?.changed) return 'article_changed';
		if (item.pending) return 'edit_waiting';
		// Its own last Check failed (a conflict found on a re-run, say): it still serves, and needs work.
		if (item.check && !item.check.ok) return 'serving_check_failed';
		return item.autoSend ? 'auto_send' : 'draft';
	}
	if (item.article?.changed) return 'article_changed';
	if (!item.check || !item.check.current) return 'needs_check';
	return item.check.ok ? 'ready' : 'check_failed';
};

export const stageBadge = (item: KnowledgeItem): { label: string; variant: BadgeVariant } => STAGES[stageOf(item)];

export interface EditableContent {
	question: string;
	answer: string;
	intent: string;
	useWhen: string;
	paraphrases: string[];
	tests: string[];
	notWhen: string[];
	article: KnowledgeArticleLink | null;
}

/** What the editor works on: an edit waiting for Check when there is one, else what serves. */
export const editableOf = (item: KnowledgeItem): EditableContent => item.pending ?? item;

/** What a Check means for customers: on a promoted item an edit goes live only once it passes. */
export const checkOutcomeOf = (before: KnowledgeItem, after: KnowledgeItem): { ok: boolean; message: string } => {
	if (before.state !== 'promoted') {
		return after.check?.ok ? { ok: true, message: 'Check passed' } : { ok: false, message: 'Check found something to fix' };
	}
	if (after.pending) return { ok: false, message: `Check failed: customers still get version ${after.version}` };
	return { ok: true, message: `Check passed: version ${after.version} is live` };
};

export type Filter = 'all' | 'review' | 'draft' | 'auto' | 'attention';

export const FILTERS: Array<{ value: Filter; label: string }> = [
	{ value: 'all', label: 'All' },
	{ value: 'review', label: 'To review' },
	{ value: 'draft', label: 'Drafts' },
	{ value: 'auto', label: 'Auto-send' },
	{ value: 'attention', label: 'Needs attention' }
];

/** A promoted item that still serves but needs work: an edit waiting, or its own Check failed. */
const servingWithWork = (stage: Stage): boolean => stage === 'edit_waiting' || stage === 'serving_check_failed';

export const matchesFilter = (item: KnowledgeItem, filter: Filter): boolean => {
	const stage = stageOf(item);
	switch (filter) {
		case 'review':
			return item.state === 'candidate';
		// A waiting edit or a failed Check leaves the served version as it was: still a draft, or still auto-sending.
		case 'draft':
			return stage === 'draft' || (servingWithWork(stage) && !item.autoSend);
		case 'auto':
			return stage === 'auto_send' || (servingWithWork(stage) && item.autoSend);
		case 'attention':
			return servingWithWork(stage) || stage === 'article_changed' || stage === 'check_failed' || stage === 'needs_check';
		default:
			return true;
	}
};

export const matchesSearch = (item: KnowledgeItem, search: string): boolean => {
	const needle = search.trim().toLowerCase();
	if (!needle) return true;
	return [item.question, item.answer, item.intent, item.article?.name ?? ''].some((text) => text.toLowerCase().includes(needle));
};

export const SOURCE_LABELS: Record<KnowledgeSource, string> = {
	article: 'Article',
	import: 'Imported',
	ticket: 'Past tickets'
};

/** What Regenerate removes, in words: "6 questions and answers (2 serving as drafts)". */
export const regenerateLossOf = (items: KnowledgeItem[]): string => {
	const serving = items.filter((item) => item.state === 'promoted' && item.enabled).length;
	const many = items.length === 1 ? '' : 's';
	const all = `${items.length} question${many} and answer${many}`;
	return serving ? `${all} (${serving} serving as draft${serving === 1 ? '' : 's'})` : all;
};

/** "1 of 3": the run of drafts sent unchanged, for a promoted item; null otherwise. */
export const streakText = (item: KnowledgeItem): string | null =>
	item.state === 'promoted' ? `${Math.min(item.streak.count, item.streak.required)} of ${item.streak.required}` : null;

const EVENT_WORDS: Record<KnowledgeEvent['kind'], string> = {
	created: 'Created',
	edited: 'Edited',
	checked: 'Checked',
	promoted: 'Promoted',
	stopped: 'Stopped serving',
	auto_send_on: 'Auto-send turned on',
	auto_send_off: 'Auto-send turned off',
	taken_down: 'Back to draft'
};

export const eventLine = (event: KnowledgeEvent): string =>
	[EVENT_WORDS[event.kind], event.reason ? `(${event.reason})` : null, event.byName ? `by ${event.byName}` : null]
		.filter(Boolean)
		.join(' ');

/** One entry per non-empty line. */
export const linesOf = (text: string): string[] =>
	text
		.split('\n')
		.map((line) => line.trim())
		.filter(Boolean);

export const textOf = (lines: string[]): string => lines.join('\n');
